import React, { useState } from 'react';
import { parseLabelStr } from '../utils/jsonParser';
import { deleteHousekeeper, updateHousekeeper } from '../api/housekeeperApi';
import './HousekeeperCard.css';

function formatDate(val) {
  if (!val) return '';
  const s = String(val);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

export default function HousekeeperCard({ hk, onClick, userRole, navigateTo, onDelete }) {
  const status = hk.resume_status || 'draft';
  const isPublished = status === 'published';
  const isPending = status === 'pending';
  const labels = parseLabelStr(hk.label);
  const [showRemarkEdit, setShowRemarkEdit] = useState(false);
  const [remark, setRemark] = useState(hk.remark || '');
  const createdText = formatDate(hk.created_at);
  const aiReady = (hk.ai_ps_count > 0) && (hk.ai_request_count > 0);

  // 将标签按每行2个分组
  const tagRows = [];
  for (let i = 0; i < labels.length; i += 2) {
    tagRows.push(labels.slice(i, i + 2));
  }

  const handleShare = (e) => {
    e.stopPropagation();
    navigateTo('detail', { id: hk.id, showShare: true });
  };

  const handlePractical = (e) => {
    e.stopPropagation();
    navigateTo('practical', { id: hk.id });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`确定要删除阿姨"${hk.name}"吗？此操作不可恢复！`)) {
      deleteHousekeeper(hk.id)
        .then(() => {
          alert('删除成功');
          if (onDelete) onDelete();
        })
        .catch((err) => {
          alert('删除失败：' + (err.response?.data?.detail || err.message));
        });
    }
  };

  const handleToggleRemark = (e) => {
    e.stopPropagation();
    setShowRemarkEdit(!showRemarkEdit);
  };

  const handleRemarkChange = (e) => {
    const value = e.target.value;
    if (value.length <= 250) {
      setRemark(value);
    }
  };

  const handleRemarkBlur = async () => {
    setShowRemarkEdit(false);
    if (remark !== (hk.remark || '')) {
      try {
        await updateHousekeeper(hk.id, { remark });
      } catch (err) {
        console.error('保存备注失败', err);
      }
    }
  };

  return (
    <div className={`hk-card card-status-${status}`} onClick={() => !showRemarkEdit && onClick(hk)}>
      {/* 第一行：姓名（全宽） */}
      <div className="card-name-row">
        <span className="card-name">{hk.name}</span>
        {hk.out_number && <div className="card-out-number">{`NO:${hk.out_number}${createdText ? '  入库日期：' + createdText : ''}`}</div>}
      </div>

      {/* 中间区域：头像（左）+ 信息（右），顶部对齐 */}
      <div className="card-body">
        {/* 左侧：头像 + 操作按钮 */}
        <div className="card-avatar">
          {hk.photo_url ? (
            <img className="avatar-img" src={hk.photo_url} alt={hk.name} />
          ) : (
            <div className="avatar-placeholder">👤</div>
          )}
          <div className="avatar-actions">
            {(isPending || isPublished) && (
              <button className="avatar-action-btn" onClick={handlePractical}>
                <span className="action-icon">🎬</span>
                <span className="action-text">实操</span>
              </button>
            )}
            <button className="avatar-action-btn" onClick={handleToggleRemark}>
              <span className="action-icon">✏️</span>
              <span className="action-text">备注</span>
            </button>
          </div>
        </div>

        {/* 右侧：信息区域 */}
        <div className="card-content">
          {/* 年龄 角色 简历状态 */}
          <div className="card-header">
            {hk.age && <span className="card-age">{hk.age}岁</span>}
            {hk.role && <span className="card-role">{hk.role}</span>}
            <span className={`status-tag ${status}`}>
              {status === 'draft' ? '草稿' : status === 'pending' ? '待发布' : '已发布'}
            </span>
            <span className={`ai-status-tag ${aiReady ? 'ready' : 'processing'}`}>{aiReady ? '可发布' : '处理中'}</span>
          </div>

          {/* 籍贯 学历 生肖 星座 */}
          <div className="card-info-row">
            {[hk.birthplace, hk.education, hk.chinese_zodiac && `属${hk.chinese_zodiac}`, hk.zodiac_sign]
              .filter(Boolean)
              .map((text, i) => (
                <span key={i} className="info-item">
                  {i > 0 && <span className="info-dot">·</span>}
                  <span className="info-text-red">{text}</span>
                </span>
              ))}
          </div>

          {/* 手机号码 */}
          {hk.phone && (
            <div className="card-phone-row">
              <span className="phone-icon">📱</span>
              <span className="info-text">{hk.phone}</span>
            </div>
          )}

          {/* 标签区域：每行2个，白底红字 */}
          {tagRows.length > 0 && (
            <div className="card-tags">
              {tagRows.map((row, ri) => (
                <div key={ri} className="tags-row">
                  {row.map((label, ti) => (
                    <span key={ti} className="mini-tag label-tag">{label}</span>
                  ))}
                  {row.length < 2 && <span className="tag-spacer" />}
                </div>
              ))}
            </div>
          )}

          {/* 管理员删除按钮 */}
          {userRole === 'admin' && (
            <div className="admin-delete-row">
              <button className="delete-btn" onClick={handleDelete}>
                <span className="delete-icon">🗑️</span>
                <span className="delete-text">删除</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 备注区域：最下边，左边与头像左边对齐 */}
      {(hk.remark || showRemarkEdit) && (
        <div className="remark-section" onClick={(e) => e.stopPropagation()}>
          {showRemarkEdit ? (
            <>
              <textarea
                className="remark-input"
                placeholder="请输入备注信息（最多250字）"
                value={remark}
                onChange={handleRemarkChange}
                onBlur={handleRemarkBlur}
                maxLength={250}
                autoFocus
              />
              <div className="remark-char-count">{remark.length}/250</div>
            </>
          ) : (
            <div className="remark-display">{hk.remark}</div>
          )}
        </div>
      )}

      {/* 分享按钮（已发布状态，右下角） */}
      {isPublished && (
        <div className="share-btn-area">
          <button className="share-btn" onClick={handleShare}>
            <span className="share-icon">🔗</span>
            <span className="share-text">分享</span>
          </button>
        </div>
      )}
    </div>
  );
}
